import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocketASR } from './useWebSocketASR';
import { useWebSpeechRecognition } from './useWebSpeechRecognition';

export type RecognitionMode = 'websocket' | 'web-speech';

interface UseVoiceRecognitionOptions {
  mode?: RecognitionMode;
  onTranscriptUpdate?: (text: string, isFinal: boolean) => void;
  onError?: (error: string | Error) => void;
  onEnd?: (finalText: string) => void;
  overWords?: string[]; // 结束词列表
  silenceTimeout?: number; // 静音超时时间（毫秒），默认 3000ms
}

const CONNECTION_STATUS = {
  connected: {
    value: 'connected',
    label: '已连接',
    color: 'var(--ant-color-success)',
  },
  recording: {
    value: 'recording',
    label: '录音中',
    color: 'var(--ant-color-success)',
  },
  connecting: {
    value: 'connecting',
    label: '连接中',
    color: 'var(--ant-color-warning)',
  },
  error: {
    value: 'error',
    label: '错误',
    color: 'var(--ant-color-error)',
  },
  idle: {
    value: 'idle',
    label: '未连接',
    color: 'var(--ant-text-secondary)',
  },
  default: {
    value: 'default',
    label: '',
    color: 'var(--ant-text-secondary)',
  },
};

/**
 * 统一的语音识别 Hook
 * 支持 WebSocket ASR 和 Web Speech API 两种方式
 */
export const useVoiceRecognition = (
  options: UseVoiceRecognitionOptions = {},
) => {
  const {
    mode = 'websocket',
    onTranscriptUpdate,
    onError,
    onEnd,
    overWords = ['结束', '发送', 'over'],
    silenceTimeout = 3000, // 默认3秒
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [currentMode, setCurrentMode] = useState<RecognitionMode>(mode);
  const [interimText, setInterimText] = useState('');
  const finalTextRef = useRef('');
  const isEnded = useRef(false);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 清除静音计时器
   */
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  /**
   * 启动静音计时器
   */
  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      console.log('[VoiceRecognition] 检测到静音超时，自动停止录音');
      isEnded.current = true;
      setIsListening(false);

      // 停止录音
      if (currentMode === 'websocket') {
        webSocketASR.stopRecording();
      } else {
        webSpeechAPI.stop();
      }

      // 发送最终文本
      if (finalTextRef.current) {
        onEnd?.(finalTextRef.current);
      }
    }, silenceTimeout);
  }, [clearSilenceTimer, silenceTimeout, currentMode, onEnd]);

  /**
   * 检查是否包含结束词
   */
  const checkOverWords = useCallback(
    (text: string): string | null => {
      for (const word of overWords) {
        if (text.toLowerCase().endsWith(word.toLowerCase())) {
          return word;
        }
      }
      return null;
    },
    [overWords],
  );

  /**
   * WebSocket ASR Hook
   */
  const webSocketASR = useWebSocketASR({
    // autoReconnect: true,
    onStart: () => {
      isEnded.current = false;
      // startSilenceTimer(); // 开始静音检测
    },
    onTranscript: (text, isFinal) => {
      if (isEnded.current) return;

      setInterimText(text);

      // 重置静音计时器
      startSilenceTimer();

      // 检查结束词
      const endWord = checkOverWords(text);
      if (endWord) {
        const finalText = text.slice(0, -endWord.length).trim();
        finalTextRef.current = finalText;
        console.log('[VoiceRecognition] 检测到结束词:', endWord);
        console.log('[VoiceRecognition] 最终文本:', finalText);
        isEnded.current = true;
        clearSilenceTimer(); // 清除计时器
        webSocketASR.stopRecording(); // 只停止录音，保持连接
        onEnd?.(finalText);
      } else {
        console.log(
          `[VoiceRecognition] ${isFinal ? '最终' : '临时'}结果:`,
          text,
        );
        finalTextRef.current = text;
        onTranscriptUpdate?.(text, isFinal);
      }
    },
    onError: (error) => {
      console.error('[VoiceRecognition] WebSocket ASR 错误:', error);
      clearSilenceTimer(); // 清除计时器
      setIsListening(false);
      onError?.(error);
    },
    onEnd: () => {
      console.log('[VoiceRecognition] WebSocket ASR 录音结束');
      clearSilenceTimer(); // 清除计时器
      setIsListening(false);
      if (!isEnded.current && finalTextRef.current) {
        onEnd?.(finalTextRef.current);
        isEnded.current = true;
      }
    },
  });

  /**
   * Web Speech API Hook
   */
  const webSpeechAPI = useWebSpeechRecognition({
    onTranscript: (text, isFinal) => {
      // 重置静音计时器
      startSilenceTimer();

      if (isFinal) {
        // 最终结果
        const endWord = checkOverWords(text);
        if (endWord) {
          const finalText = (
            finalTextRef.current + text.slice(0, -endWord.length)
          ).trim();
          console.log('[VoiceRecognition] 检测到结束词:', endWord);
          finalTextRef.current = finalText;
          clearSilenceTimer(); // 清除计时器
          webSpeechAPI.stop();
        } else {
          finalTextRef.current += text;
          console.log('[VoiceRecognition] 最终结果:', text);
          onTranscriptUpdate?.(finalTextRef.current, true);
        }
      } else {
        // 临时结果
        console.log('[VoiceRecognition] 临时结果:', text);
        setInterimText(text);
        onTranscriptUpdate?.(finalTextRef.current + text, false);
      }
    },
    onError: (error) => {
      console.error('[VoiceRecognition] Web Speech API 错误:', error);
      clearSilenceTimer(); // 清除计时器
      setIsListening(false);
      onError?.(error);
    },
    onEnd: () => {
      console.log('[VoiceRecognition] Web Speech API 结束');
      clearSilenceTimer(); // 清除计时器
      setIsListening(false);
      if (finalTextRef.current) {
        onEnd?.(finalTextRef.current);
      }
    },
  });

  /**
   * 连接（仅用于 WebSocket ASR）
   */
  const connect = useCallback(async () => {
    if (currentMode === 'websocket') {
      console.log('[VoiceRecognition] 连接 WebSocket...');
      return await webSocketASR.connect();
    }
    return true;
  }, [currentMode, webSocketASR]);

  /**
   * 断开连接（仅用于 WebSocket ASR）
   */
  const disconnect = useCallback(() => {
    if (currentMode === 'websocket') {
      console.log('[VoiceRecognition] 断开 WebSocket...');
      webSocketASR.disconnect();
    }
  }, [currentMode, webSocketASR]);

  /**
   * 开始识别
   */
  const start = useCallback(async () => {
    // 重置状态
    finalTextRef.current = '';
    setInterimText('');
    isEnded.current = false;

    console.log('[VoiceRecognition] 开始录音, 模式:', currentMode);

    let success = false;
    if (currentMode === 'websocket') {
      // 确保已连接
      if (!webSocketASR.isConnected) {
        await webSocketASR.connect();
      }
      // 开始录音
      success = await webSocketASR.startRecording();
    } else {
      success = webSpeechAPI.start();
      // Web Speech API 需要手动启动静音计时器（因为没有 onStart 回调）
      if (success) {
        startSilenceTimer();
      }
    }

    if (success) {
      setIsListening(true);
    }

    return success;
  }, [currentMode, webSocketASR, webSpeechAPI, startSilenceTimer]);

  /**
   * 停止识别
   */
  const stop = useCallback(() => {
    console.log('[VoiceRecognition] 停止录音');
    clearSilenceTimer(); // 清除计时器
    setIsListening(false);

    if (currentMode === 'websocket') {
      webSocketASR.stopRecording(); // 只停止录音，保持连接
    } else {
      webSpeechAPI.stop();
    }
  }, [currentMode, webSocketASR, webSpeechAPI, clearSilenceTimer]);

  /**
   * 切换识别模式
   */
  const switchMode = useCallback(
    (newMode: RecognitionMode) => {
      console.log('[VoiceRecognition] 切换模式:', currentMode, '->', newMode);

      // 如果正在识别，先停止
      if (isListening) {
        stop();
      }

      // 断开旧模式的连接
      if (currentMode === 'websocket') {
        webSocketASR.disconnect();
      } else {
        webSpeechAPI.cleanup();
      }

      // 切换到新模式（会触发 useEffect 自动连接）
      setCurrentMode(newMode);
    },
    [currentMode, isListening, stop, webSocketASR, webSpeechAPI],
  );

  /**
   * 自动管理连接：当模式为 websocket 时保持连接
   */
  useEffect(() => {
    if (currentMode === 'websocket') {
      // 切换到 websocket 模式时，自动连接
      console.log('[VoiceRecognition] 模式切换到 WebSocket，自动连接...');
      webSocketASR.connect();
    }

    return () => {
      // 组件卸载或模式切换时清理
      clearSilenceTimer(); // 清除计时器
      if (currentMode === 'websocket') {
        console.log('[VoiceRecognition] 清理 WebSocket 连接');
        webSocketASR.disconnect();
      } else {
        webSpeechAPI.cleanup();
      }
    };
  }, [currentMode]); // 只依赖 currentMode，避免无限循环

  /**
   * 检查当前模式是否支持
   */
  const isCurrentModeSupported = useCallback(() => {
    if (currentMode === 'websocket') {
      return webSocketASR.isSupported();
    } else {
      return webSpeechAPI.isSupported();
    }
  }, [currentMode, webSocketASR, webSpeechAPI]);

  return {
    // 状态
    isListening,
    currentMode,
    interimText,
    finalText: finalTextRef.current,
    isConnected: currentMode === 'websocket' ? webSocketASR.isConnected : true,

    // WebSocket ASR 特定状态
    connectionStatus:
      currentMode === 'websocket'
        ? CONNECTION_STATUS[webSocketASR.status] || CONNECTION_STATUS.idle
        : CONNECTION_STATUS.idle,
    isConnecting:
      currentMode === 'websocket' ? webSocketASR.isConnecting : false,
    isRecording:
      currentMode === 'websocket' ? webSocketASR.isRecording : isListening,
    hasError: currentMode === 'websocket' ? webSocketASR.hasError : false,

    // 连接管理（仅 WebSocket 模式有效）
    connect,
    disconnect,

    // 录音控制
    start,
    stop,

    // 模式切换
    switchMode,

    // 工具方法
    isSupported: isCurrentModeSupported,
  };
};
